/**
 * ConversationMediaGallery - Modal para visualizar todos os anexos da conversa
 * Suporta filtros por tipo, grid responsivo, infinite scroll e integração com ImageLightbox
 * 
 * Versão: 2.0.0 - Refatorado para receber dados via props + paginação server-side
 */

import { useState, useRef, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { MediaThumbnail } from './MediaThumbnail';
import { ImageLightbox, MediaItem } from './ImageLightbox';
import { getStorageUrl } from '@/lib/storage';
import { Download, Loader2 } from 'lucide-react';
import { ConversationAttachment } from '@/hooks/useConversationAttachments';

// Tipos de filtro disponíveis (áudio excluído da galeria)
type FilterType = 'all' | 'image' | 'video' | 'document';

interface ConversationMediaGalleryProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  attachments: ConversationAttachment[];
  totalCount: number;
  isLoading: boolean;
  isFetchingMore: boolean;
  hasMorePages: boolean;
  onLoadMore: () => void;
}

export function ConversationMediaGallery({
  open,
  onOpenChange,
  attachments,
  totalCount,
  isLoading,
  isFetchingMore,
  hasMorePages,
  onLoadMore,
}: ConversationMediaGalleryProps) {
  const [filter, setFilter] = useState<FilterType>('all');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxMediaItems, setLightboxMediaItems] = useState<MediaItem[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  
  // Ref para infinite scroll via Intersection Observer
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // =========================================================================
  // Intersection Observer para infinite scroll automático
  // =========================================================================
  useEffect(() => {
    // Não observa se modal fechado, sem mais páginas ou já carregando
    if (!open || !hasMorePages || isFetchingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Quando o elemento trigger fica visível, carrega próxima página
        if (entries[0].isIntersecting) {
          onLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [open, hasMorePages, isFetchingMore, onLoadMore]);

  // =========================================================================
  // Filtragem e contagem (memoizado para performance)
  // =========================================================================
  
  // Filtra anexos pelo tipo selecionado
  const filteredAttachments = useMemo(() => {
    return attachments.filter(a => {
      if (filter === 'all') return true;
      if (filter === 'image') return a.type === 'image' || a.type === 'sticker';
      return a.type === filter;
    });
  }, [attachments, filter]);

  // Contadores por tipo para os botões de filtro
  const counts = useMemo(() => ({
    all: attachments.length,
    image: attachments.filter(a => a.type === 'image' || a.type === 'sticker').length,
    video: attachments.filter(a => a.type === 'video').length,
    document: attachments.filter(a => a.type === 'document').length,
  }), [attachments]);

  // =========================================================================
  // Handlers
  // =========================================================================
  
  // Handler para clique em anexo - abre lightbox ou download
  const handleAttachmentClick = (attachment: ConversationAttachment) => {
    // Tipos que podem ser visualizados no lightbox
    const viewableTypes = ['image', 'sticker', 'video'];
    
    if (viewableTypes.includes(attachment.type)) {
      // Filtra apenas mídias visualizáveis para navegação no lightbox
      const viewableAttachments = attachments.filter(a => viewableTypes.includes(a.type));
      
      // Mapeia para MediaItem com tipo correto
      const mediaItems: MediaItem[] = viewableAttachments.map(a => ({
        url: getStorageUrl(a.url) || a.url,
        type: a.type === 'video' ? 'video' : 'image',
      }));
      
      // Encontra índice do item clicado
      const index = viewableAttachments.findIndex(a => a.id === attachment.id);
      
      setLightboxMediaItems(mediaItems);
      setLightboxIndex(index >= 0 ? index : 0);
      setLightboxOpen(true);
    } else {
      // Documentos: abre em nova aba para download/visualização
      const url = getStorageUrl(attachment.url) || attachment.url;
      window.open(url, '_blank');
    }
  };

  // Botões de filtro (áudio removido)
  const filterButtons: { value: FilterType; label: string }[] = [
    { value: 'all', label: 'Todos' },
    { value: 'image', label: 'Imagens' },
    { value: 'video', label: 'Vídeos' },
    { value: 'document', label: 'Documentos' },
  ];

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>
              Mídias e documentos
              {totalCount > 0 && (
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  ({attachments.length} de {totalCount} carregados)
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          {/* Filtros por tipo */}
          <div className="flex gap-2 flex-wrap pb-2 border-b">
            {filterButtons.map(btn => (
              <Button
                key={btn.value}
                variant={filter === btn.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter(btn.value)}
                disabled={counts[btn.value] === 0}
              >
                {btn.label}
                {counts[btn.value] > 0 && (
                  <span className="ml-1.5 text-xs opacity-70">
                    ({counts[btn.value]})
                  </span>
                )}
              </Button>
            ))}
          </div>

          {/* Grid de anexos com infinite scroll */}
          <ScrollArea className="h-[calc(80vh-140px)] pr-4">
            {isLoading ? (
              // Skeleton durante carregamento inicial
              <div className="grid grid-cols-4 gap-3 p-2">
                {[...Array(8)].map((_, i) => (
                  <Skeleton key={i} className="aspect-square rounded-lg" />
                ))}
              </div>
            ) : filteredAttachments.length === 0 ? (
              // Estado vazio
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <p>Nenhum arquivo encontrado</p>
              </div>
            ) : (
              <>
                {/* Grid de thumbnails */}
                <div className="grid grid-cols-4 gap-3 p-2">
                  {filteredAttachments.map(attachment => (
                    <div key={attachment.id} className="relative group">
                      <MediaThumbnail
                        type={attachment.type}
                        url={attachment.url}
                        thumbnailUrl={attachment.thumbnail_url}
                        filename={attachment.filename}
                        mimeType={attachment.mime_type}
                        size={120}
                        className="w-full aspect-square"
                        onClick={() => handleAttachmentClick(attachment)}
                      />
                      {/* Overlay com nome do arquivo */}
                      {attachment.filename && (
                        <div className="absolute inset-x-0 bottom-0 p-1 bg-gradient-to-t from-black/60 to-transparent rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity">
                          <p className="text-[10px] text-white truncate">
                            {attachment.filename}
                          </p>
                        </div>
                      )}
                      {/* Botão de download */}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute top-1 right-1 h-6 w-6 bg-black/40 hover:bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          const url = getStorageUrl(attachment.url) || attachment.url;
                          window.open(url, '_blank');
                        }}
                      >
                        <Download className="h-3 w-3 text-white" />
                      </Button>
                    </div>
                  ))}
                </div>

                {/* Trigger para infinite scroll + indicador de carregamento */}
                {hasMorePages && (
                  <div 
                    ref={loadMoreRef} 
                    className="flex justify-center py-4"
                  >
                    {isFetchingMore ? (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm">Carregando mais...</span>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={onLoadMore}
                      >
                        Carregar mais
                      </Button>
                    )}
                  </div>
                )}
              </>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Lightbox para imagens e vídeos */}
      <ImageLightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        mediaItems={lightboxMediaItems}
        initialIndex={lightboxIndex}
      />
    </>
  );
}
